/**
 * FFS transition (1 Jan 2027) — TRUS-724.
 *
 * "Which of my members move to Medi-Cal fee-for-service on 1 January 2027?"
 * Members with Unsatisfactory Immigration Status leave managed care that day.
 * Trust does not record immigration status; the server estimates the cohort
 * from each member's latest Medi-Cal aid-code benefits scope and says so in
 * `basis`. The page repeats that in plain words above the numbers.
 *
 * GET /api/analytics/ffs-transition-cohort
 *   scope=org       counts + by program + by plan + paginated members (own org)
 *   scope=platform  platform admins only: counts across orgs, small cells hidden
 *
 * The server sends codes (group, unknown_reason), never sentences, so every
 * string here goes through t() and has an es.json entry.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, RefreshCw } from 'lucide-react';
import { authFetch } from '@/lib/authFetch';
import { PopoutNoAccess } from '@/components/popout/PopoutNoAccess';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  PageContainer,
  PageHeader,
  PageState,
  SectionHeader,
  StatTile,
  StatTileGrid,
} from '@/components/weavein';

type Group = 'RESTRICTED' | 'EMERGENCY_ONLY' | 'FULL_SCOPE' | 'UNKNOWN';
type UnknownReason = 'NO_CHECK' | 'STALE' | 'NO_SCOPE';

const GROUPS: Group[] = ['RESTRICTED', 'EMERGENCY_ONLY', 'FULL_SCOPE', 'UNKNOWN'];
const PLATFORM_ROLES = new Set(['super_admin', 'ops_admin']);
const PAGE_SIZE = 50;

interface Basis {
  code: string;
  is_immigration_status: boolean;
  transition_date: string;
  stale_after_days: number;
  as_of: string;
}

interface CountsRow {
  counts: Record<Group, number>;
  total: number;
}

interface ProgramRow extends CountsRow {
  program_id: number | null;
  program_name: string | null;
}

interface PlanRow extends CountsRow {
  plan_name: string | null;
}

interface MemberRow {
  member_id: number;
  display_name: string | null;
  group: Group;
  unknown_reason: UnknownReason | null;
  plan_name: string | null;
  last_checked_at: string | null;
  programs: { id: number; name: string | null }[];
}

interface OrgResponse {
  mode: 'org';
  basis: Basis;
  counts: Record<Group, number>;
  total: number;
  transitioning: number;
  by_program: ProgramRow[];
  by_plan: PlanRow[];
  members: { items: MemberRow[]; page: number; page_size: number; total: number };
}

interface Cell {
  value: number | null;
  suppressed: boolean;
}

interface PlatformResponse {
  mode: 'platform';
  basis: Basis;
  org_count: number;
  counts: Record<Group, Cell>;
  total: Cell;
  transitioning: Cell;
  by_plan: { plan_name: string | null; counts: Record<Group, Cell>; total: Cell }[];
}

const groupTone: Record<Group, 'attention' | 'danger' | 'success' | 'neutral'> = {
  RESTRICTED: 'attention',
  EMERGENCY_ONLY: 'danger',
  FULL_SCOPE: 'success',
  UNKNOWN: 'neutral',
};

const chipClass: Record<Group, string> = {
  RESTRICTED: 'bg-attention-soft text-attention-ink',
  EMERGENCY_ONLY: 'bg-danger-soft text-ink-700',
  FULL_SCOPE: 'bg-success-soft text-success-ink',
  UNKNOWN: 'bg-paper-2 text-ink-500',
};

const card = 'rounded-[10px] border border-v2border bg-surface shadow-v2-1';
const th = 'px-3 py-2 text-left font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-500';
const td = 'px-3 py-2 text-[13px] text-ink';

/** Date-only display, no timezone shift (the check time is stored as UTC). */
function formatDate(iso: string | null, lang: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(lang, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export default function FfsTransitionCohortPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isPlatformAdmin = !!user && PLATFORM_ROLES.has(String(user.role || '').toLowerCase());

  const [view, setView] = useState<'org' | 'platform'>('org');
  const [group, setGroup] = useState<Group | ''>('');
  const [programId, setProgramId] = useState<string>('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<OrgResponse | PlatformResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // TRUS-739: the API 403s `capability_disabled` when the active org does not
  // have ffs_transition (e.g. turned off since the capability list was cached).
  const [capabilityDisabled, setCapabilityDisabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ scope: view, page: String(page), page_size: String(PAGE_SIZE) });
    if (view === 'org') {
      if (group) params.set('group', group);
      if (programId !== '') params.set('program_id', programId);
    }
    try {
      const res = await authFetch(`/api/analytics/ffs-transition-cohort?${params.toString()}`);
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        if (res.status === 403 && body?.detail?.code === 'capability_disabled') {
          setCapabilityDisabled(true);
          setData(null);
          return;
        }
        const detail = body?.detail;
        throw new Error(
          (typeof detail === 'string' ? detail : detail?.message) || `${res.status} ${res.statusText}`,
        );
      }
      setData(body as OrgResponse | PlatformResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [view, group, programId, page]);

  useEffect(() => {
    load();
  }, [load]);

  const groupLabel = (g: Group) => t(`ffsTransition.group.${g}`);
  const programLabel = (name: string | null) => name || t('ffsTransition.noProgram');
  const planLabel = (name: string | null) => name || t('ffsTransition.unknownPlan');
  const staleDays = data?.basis.stale_after_days ?? 90;

  const reasonLabel = (r: UnknownReason | null) => {
    if (r === 'NO_CHECK') return t('ffsTransition.reason.NO_CHECK');
    if (r === 'STALE') return t('ffsTransition.reason.STALE', { days: staleDays });
    if (r === 'NO_SCOPE') return t('ffsTransition.reason.NO_SCOPE');
    return null;
  };

  const drill = (g: Group | '', p: number | null | undefined) => {
    setGroup(g);
    setProgramId(p === undefined ? '' : String(p ?? 0));
    setPage(1);
  };

  const viewTabs = useMemo(
    () =>
      isPlatformAdmin
        ? [
            { key: 'org', label: t('ffsTransition.view.org') },
            { key: 'platform', label: t('ffsTransition.view.platform') },
          ]
        : undefined,
    [isPlatformAdmin, t],
  );

  const cellText = (c: Cell) => (c.suppressed ? t('ffsTransition.suppressed') : String(c.value ?? 0));

  const header = (
    <PageHeader
      eyebrow={t('ffsTransition.eyebrow')}
      title={t('ffsTransition.title')}
      description={t('ffsTransition.description')}
      actions={
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden />
          {t('ffsTransition.refresh')}
        </Button>
      }
      tabs={viewTabs}
      activeTab={view}
      onTabChange={(k) => {
        setView(k as 'org' | 'platform');
        setGroup('');
        setProgramId('');
        setPage(1);
      }}
    />
  );

  const proxyNotice = (
    <div className={`${card} flex gap-3 px-4 py-3`} role="note">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-v2accent" aria-hidden />
      <div className="space-y-1 text-[13px] text-ink-500">
        <p className="text-ink">{t('ffsTransition.proxyNotice')}</p>
        <p>{t('ffsTransition.unknownNotice', { days: staleDays })}</p>
        {data?.mode === 'platform' && <p>{t('ffsTransition.suppressedNotice')}</p>}
      </div>
    </div>
  );

  if (capabilityDisabled) return <PopoutNoAccess />;

  let body: React.ReactNode;
  if (loading && !data) {
    body = <PageState variant="loading" title={t('ffsTransition.loading')} />;
  } else if (error) {
    body = (
      <PageState
        variant="error"
        title={t('ffsTransition.error')}
        message={error}
        action={
          <Button variant="outline" size="sm" onClick={load}>
            {t('ffsTransition.retry')}
          </Button>
        }
      />
    );
  } else if (data?.mode === 'platform') {
    body = (
      <>
        {proxyNotice}
        <StatTileGrid>
          <StatTile
            label={t('ffsTransition.transitioning')}
            value={cellText(data.transitioning)}
            caption={t('ffsTransition.orgCount', { count: data.org_count })}
            tone="attention"
          />
          {GROUPS.map((g) => (
            <StatTile key={g} label={groupLabel(g)} value={cellText(data.counts[g])} tone={groupTone[g]} />
          ))}
        </StatTileGrid>
        <SectionHeader title={t('ffsTransition.byPlan')} />
        <CountsTable
          firstColumn={t('ffsTransition.plan')}
          rows={data.by_plan.map((r) => ({
            key: r.plan_name ?? '__none__',
            label: planLabel(r.plan_name),
            cells: GROUPS.map((g) => cellText(r.counts[g])),
            total: cellText(r.total),
          }))}
          groupLabel={groupLabel}
          totalLabel={t('ffsTransition.total')}
        />
      </>
    );
  } else if (data?.mode === 'org') {
    const pages = Math.max(1, Math.ceil(data.members.total / data.members.page_size));
    body = (
      <>
        {proxyNotice}
        {data.total === 0 ? (
          <PageState variant="empty" title={t('ffsTransition.emptyTitle')} message={t('ffsTransition.emptyMessage')} />
        ) : (
          <>
            <StatTileGrid>
              <button type="button" className="text-left" onClick={() => drill('', undefined)}>
                <StatTile
                  label={t('ffsTransition.transitioning')}
                  value={data.transitioning}
                  caption={t('ffsTransition.ofTotal', { total: data.total })}
                  tone="attention"
                  className="h-full"
                />
              </button>
              {GROUPS.map((g) => (
                <button
                  key={g}
                  type="button"
                  className="text-left"
                  aria-pressed={group === g}
                  onClick={() => drill(g, programId === '' ? undefined : Number(programId))}
                >
                  <StatTile
                    label={groupLabel(g)}
                    value={data.counts[g]}
                    caption={t(`ffsTransition.groupCaption.${g}`)}
                    delta={group === g ? t('ffsTransition.filtered') : undefined}
                    tone={groupTone[g]}
                    className={`h-full ${group === g ? 'border-v2accent shadow-v2-2' : ''}`}
                  />
                </button>
              ))}
            </StatTileGrid>

            <SectionHeader title={t('ffsTransition.byProgram')} />
            <CountsTable
              firstColumn={t('ffsTransition.program')}
              rows={data.by_program.map((r) => ({
                key: String(r.program_id ?? 'none'),
                label: programLabel(r.program_name),
                cells: GROUPS.map((g) => String(r.counts[g])),
                total: String(r.total),
                onCell: (i: number | null) => drill(i === null ? '' : GROUPS[i], r.program_id),
              }))}
              groupLabel={groupLabel}
              totalLabel={t('ffsTransition.total')}
            />

            {data.by_plan.length > 0 && (
              <>
                <SectionHeader title={t('ffsTransition.byPlan')} />
                <CountsTable
                  firstColumn={t('ffsTransition.plan')}
                  rows={data.by_plan.map((r) => ({
                    key: r.plan_name ?? '__none__',
                    label: planLabel(r.plan_name),
                    cells: GROUPS.map((g) => String(r.counts[g])),
                    total: String(r.total),
                  }))}
                  groupLabel={groupLabel}
                  totalLabel={t('ffsTransition.total')}
                />
              </>
            )}

            <SectionHeader
              title={t('ffsTransition.members')}
              action={
                group || programId !== '' ? (
                  <Button variant="ghost" size="sm" onClick={() => drill('', undefined)}>
                    {t('ffsTransition.clearFilters')}
                  </Button>
                ) : undefined
              }
            />
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-[13px] text-ink-500">
                {t('ffsTransition.filterGroup')}
                <select
                  className="rounded-md border border-v2border bg-surface px-2 py-1 text-[13px] text-ink"
                  value={group}
                  onChange={(e) => drill(e.target.value as Group | '', programId === '' ? undefined : Number(programId))}
                >
                  <option value="">{t('ffsTransition.allGroups')}</option>
                  {GROUPS.map((g) => (
                    <option key={g} value={g}>
                      {groupLabel(g)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-[13px] text-ink-500">
                {t('ffsTransition.filterProgram')}
                <select
                  className="rounded-md border border-v2border bg-surface px-2 py-1 text-[13px] text-ink"
                  value={programId}
                  onChange={(e) => {
                    setProgramId(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t('ffsTransition.allPrograms')}</option>
                  {data.by_program.map((r) => (
                    <option key={String(r.program_id ?? 0)} value={String(r.program_id ?? 0)}>
                      {programLabel(r.program_name)}
                    </option>
                  ))}
                </select>
              </label>
              <span className="text-[13px] text-ink-500">
                {t('ffsTransition.memberCount', { count: data.members.total })}
              </span>
            </div>

            <div className={`${card} overflow-x-auto`}>
              {data.members.items.length === 0 ? (
                <div className="px-4 py-8 text-center text-[13px] text-ink-500">{t('ffsTransition.noMatches')}</div>
              ) : (
                <table className="w-full">
                  <thead className="border-b border-v2border">
                    <tr>
                      <th className={th}>{t('ffsTransition.memberName')}</th>
                      <th className={th}>{t('ffsTransition.scope')}</th>
                      <th className={th}>{t('ffsTransition.programs')}</th>
                      <th className={th}>{t('ffsTransition.plan')}</th>
                      <th className={th}>{t('ffsTransition.lastChecked')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.members.items.map((m) => (
                      <tr key={m.member_id} className="border-b border-v2border-muted last:border-0 hover:bg-paper-2">
                        <td className={td}>
                          <button
                            type="button"
                            className="text-left font-medium text-v2accent-ink hover:underline"
                            onClick={() =>
                              window.dispatchEvent(new CustomEvent('open-member', { detail: { memberId: m.member_id } }))
                            }
                          >
                            {m.display_name || t('ffsTransition.unnamed')}
                          </button>
                        </td>
                        <td className={td}>
                          <span
                            className={`inline-flex h-5 items-center rounded-full px-2 font-mono text-[10px] font-semibold uppercase tracking-[0.03em] ${chipClass[m.group]}`}
                          >
                            {groupLabel(m.group)}
                          </span>
                          {m.unknown_reason && (
                            <div className="mt-0.5 text-[12px] text-ink-500">{reasonLabel(m.unknown_reason)}</div>
                          )}
                        </td>
                        <td className={td}>
                          {m.programs.length
                            ? m.programs.map((p) => programLabel(p.name)).join(', ')
                            : t('ffsTransition.noProgram')}
                        </td>
                        <td className={td}>{planLabel(m.plan_name)}</td>
                        <td className={td}>
                          {formatDate(m.last_checked_at, i18n.language) ?? t('ffsTransition.neverChecked')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {pages > 1 && (
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  {t('ffsTransition.previous')}
                </Button>
                <span className="text-[13px] text-ink-500">
                  {t('ffsTransition.pageOf', { page: data.members.page, pages })}
                </span>
                <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                  {t('ffsTransition.next')}
                </Button>
              </div>
            )}
          </>
        )}
      </>
    );
  } else {
    body = (
      <PageState
        variant="error"
        icon={<AlertTriangle className="h-6 w-6 text-attention" />}
        title={t('ffsTransition.error')}
      />
    );
  }

  return (
    <>
      {header}
      <PageContainer>
        <div className="space-y-4">{body}</div>
      </PageContainer>
    </>
  );
}

interface CountsTableRow {
  key: string;
  label: string;
  cells: string[];
  total: string;
  /** Click a cell to drill into the member list (null = the whole row). */
  onCell?: (groupIndex: number | null) => void;
}

function CountsTable({
  firstColumn,
  rows,
  groupLabel,
  totalLabel,
}: {
  firstColumn: string;
  rows: CountsTableRow[];
  groupLabel: (g: Group) => string;
  totalLabel: string;
}) {
  return (
    <div className={`${card} overflow-x-auto`}>
      <table className="w-full">
        <thead className="border-b border-v2border">
          <tr>
            <th className={th}>{firstColumn}</th>
            {GROUPS.map((g) => (
              <th key={g} className={`${th} text-right`}>
                {groupLabel(g)}
              </th>
            ))}
            <th className={`${th} text-right`}>{totalLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-v2border-muted last:border-0">
              <td className={td}>
                {r.onCell ? (
                  <button type="button" className="text-left hover:underline" onClick={() => r.onCell?.(null)}>
                    {r.label}
                  </button>
                ) : (
                  r.label
                )}
              </td>
              {r.cells.map((c, i) => (
                <td key={GROUPS[i]} className={`${td} text-right tabular-nums`}>
                  {r.onCell && c !== '0' ? (
                    <button type="button" className="text-v2accent-ink hover:underline" onClick={() => r.onCell?.(i)}>
                      {c}
                    </button>
                  ) : (
                    c
                  )}
                </td>
              ))}
              <td className={`${td} text-right font-medium tabular-nums`}>{r.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
